"use client"

import { useState, useCallback, useRef } from "react"
import { Loader2 } from "lucide-react"
import { type Sound } from "@/lib/supabase"
import SoundCard from "@/components/ui/sound-card"
import { announce } from "@/lib/announce"

interface CategoryLoadMoreProps {
  categoryName: string
  initialOffset: number
  batchSize: number
}

interface SoundsListResponse {
  sounds: Sound[]
  total: number
}

// Deliberately load-more, not numbered pages like /sounds (hooks/usePaginatedSounds.ts) —
// a single category's result set is small enough that incremental append reads better than a pager; don't converge the two paradigms.
export default function CategoryLoadMore({ categoryName, initialOffset, batchSize }: CategoryLoadMoreProps) {
  const [sounds, setSounds] = useState<Sound[]>([])
  const offsetRef = useRef(initialOffset)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)

  const loadMore = useCallback(async () => {
    if (loading) return
    setLoading(true)
    try {
      // initialOffset and batchSize are both INITIAL_LIMIT (24), so the running
      // offset stays a multiple of batchSize and maps cleanly onto API pages.
      const page = Math.floor(offsetRef.current / batchSize) + 1
      const params = new URLSearchParams({
        category: categoryName,
        page: String(page),
        per_page: String(batchSize),
        sort: "downloads",
        order: "desc",
      })
      const res = await fetch(`/api/sounds?${params.toString()}`)
      if (!res.ok) {
        setHasMore(false)
        return
      }
      const data = (await res.json()) as SoundsListResponse
      const newSounds = data.sounds ?? []
      setSounds(prev => [...prev, ...newSounds])
      offsetRef.current += newSounds.length
      setHasMore(offsetRef.current < (data.total ?? 0) && newSounds.length > 0)
      announce(
        newSounds.length > 0
          ? `Loaded ${newSounds.length} more sound${newSounds.length === 1 ? "" : "s"}`
          : "No more sounds to load",
        "polite"
      )
    } catch {
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }, [categoryName, batchSize, loading])

  return (
    <>
      {sounds.length > 0 && (
        <div
          // SoundCard is a full-width ROW, not a card. See the note in
          // category-subcollection-tabs.tsx: four to a line truncated every
          // title to a single character. gap-0 so the appended rows continue
          // the hairline rhythm of the list above rather than starting a
          // second, detached block.
          className="grid grid-cols-1 gap-0"
          aria-busy={loading}
        >
          {sounds.map((sound) => (
            <SoundCard key={sound.id} sound={sound} showCategory={false} />
          ))}
        </div>
      )}
      {hasMore && (
        <div className="text-center mt-12">
          <button type="button"
            onClick={loadMore}
            disabled={loading}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--tls-surface-elevated)] hover:bg-[var(--tls-surface-hover)] border border-[var(--tls-border-subtle)] disabled:opacity-50 text-[var(--tls-fg-strong)] font-medium rounded-[var(--radius-pill)] transition-[background-color,border-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]"
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Loading more sounds…
              </>
            ) : (
              "Load More Sounds"
            )}
          </button>
        </div>
      )}
    </>
  )
}
